import React, { useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import FieldContainer from '@common/FieldContainer';
import { useToast } from '@common/toast';
import { useUniversityEmailCheck, useUniversityEmailCodeSend } from '@repository/partnership/usePartnership';
import { emailValidator, valueNotRequired, valueRequired } from '@utils/validators/validators';
import useTimer from '@utils/hooks/useTimer';

import classnames from 'classnames/bind';
import styles from '../premium.module.scss';
const cx = classnames.bind(styles);

const CompletedEmailAuth = () => {
  return (
    <div className={cx('authArea')}>
      <div className={cx('authInner')}>
        <input type="text" className={cx('input')} placeholder={'인증이 완료되었습니다.'} disabled={true} />
        <span className={cx('btnInfoArea')}>
          <button type="button" className={cx('btnInfo', 'complete')} disabled={true}>
            인증완료
          </button>
        </span>
      </div>
      <div className={cx('authInner')}>
        <input type="text" className={cx('input')} placeholder={'인증코드 인증이 완료되었습니다.'} disabled={true} />
        <span className={cx('btnInfoArea')}>
          <button type="button" className={cx('btnInfo', 'complete')} disabled={true}>
            인증완료
          </button>
        </span>
      </div>
    </div>
  );
};

const schoolFieldName = 'school';
const majorFieldName = 'major';
const emailFieldName = 'email';
const tokenFieldName = 'token';
const studentIdFieldName = 'studentId';

type CertStateType = 'ING' | 'END' | 'SUCCEESS' | null;

const EmailAuth = () => {
  const Toast = useToast();
  const { mutateAsync: sendEmailCode } = useUniversityEmailCodeSend();
  const { mutateAsync: checkEmailCode } = useUniversityEmailCheck();
  const [certState, setCertState] = useState<CertStateType>(null);
  const {
    trigger,
    setError,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();

  //제휴 대학 이메일
  const { field: emailField, fieldState: emailMeta } = useController({
    name: emailFieldName,
    defaultValue: '',
    rules: {
      ...valueRequired,
      validate: (value: string) => {
        return emailValidator(value);
      },
    },
  });

  const errorMessage = `${errors?.email?.message}`;

  // emailCheck라는 name이 할당되어있지 않아 setValue로 수정시 값이 변경되지 않습니다.
  const { field: emailCheck } = useController({
    name: 'emailCheck',
  });

  //인증 코드
  const { field: tokenField, fieldState: tokenMeta } = useController({
    name: tokenFieldName,
    defaultValue: '',
    rules: valueNotRequired,
  });

  const resetEmailCheck = () => {
    if (certState !== 'ING') return;
    setValue(emailFieldName, '');
    setCertState(null);
    onStop();
  };

  const { getRemainingTimeFormat, onStart, onStop } = useTimer({
    time: 10 * 60,
    start: false,
    onCountUp: () => setCertState('END'),
  });

  //이메일 인증 코드 전송
  const onClickRequestCert = async () => {
    resetEmailCheck();

    const isSuccess = await trigger([emailFieldName, schoolFieldName, majorFieldName, studentIdFieldName]);
    if (isSuccess) {
      const schoolCode = getValues(schoolFieldName);
      try {
        const result = await sendEmailCode({ schoolCode: schoolCode.value, email: emailField.value });
        if (result) {
          Toast({
            size: 'xlarge',
            iconType: 'check',
            type: 'success',
            content: (
              <span>
                인증 메일이 발송되었습니다.
                <p className={cx('toastDesc')}>발송된 메일을 통해 인증코드를 입력하시면 인증이 완료됩니다.</p>
              </span>
            ),
          });
          setCertState('ING');
          onStart();
        } else setError(emailFieldName, { message: '올바른 이메일 형식이 아닙니다.' });
      } catch (e: any) {
        switch (e.response.data.errorCode) {
          case 'B902':
            setError(emailFieldName, { message: '올바른 이메일 형식이 아닙니다.' });
            return;
          case 'B903':
            setError(emailFieldName, { message: '제휴 대학 이메일 주소가 아닙니다.' });
            return;
          case 'B904':
            setError(emailFieldName, { message: '이미 인증에 활용된 이메일 주소입니다.' });
            return;
        }
      }
    }
  };

  const onClickCheckCert = async () => {
    try {
      const result = await checkEmailCode({ email: emailField.value, token: tokenField.value });

      if (result) {
        Toast({
          iconType: 'check',
          type: 'success',
          content: '인증이 완료되었습니다.',
        });
        setCertState('SUCCEESS');
        emailCheck.onChange(true);
        onStop();
      }
    } catch (e: any) {
      switch (e.response.data.errorCode) {
        case 'B902':
          setError(tokenFieldName, { message: '제휴 대학과 이메일 인증 정보가 다릅니다.' });
          return;
      }
    }
  };

  return (
    <FieldContainer
      name={
        // eslint-disable-next-line react/jsx-wrap-multilines
        <>
          제휴 대학
          <br />
          이메일 인증
        </>
      }
      premiumName={true}
      required={true}
    >
      {certState === 'SUCCEESS' && <CompletedEmailAuth />}
      <div className={cx('authArea', { hidden: certState === 'SUCCEESS' })}>
        <div className={cx('authInner')}>
          <input
            {...emailField}
            type="text"
            className={cx('input', { error: emailMeta.invalid, success: certState === 'SUCCEESS' })}
            placeholder={'대학교 이메일을 입력해 주세요.'}
            disabled={certState === 'ING'}
          />
          <div className={cx('btnInfoArea')}>
            <button type="button" className={cx('btnInfo')} onClick={onClickRequestCert} disabled={!emailField.value}>
              {certState === 'ING' ? '재전송' : '인증요청'}
            </button>
          </div>
          {certState === 'ING' && (
            <div className={cx('noticeTextArea')}>
              <span className={cx('noticeText', 'success')}>발송된 메일을 통해 인증코드를 확인해 주세요.</span>
            </div>
          )}
          {emailMeta.invalid && (
            <div className={cx('noticeTextArea')}>
              <span className={cx('noticeText', 'error')}>{errorMessage}</span>
            </div>
          )}
        </div>
        <div className={cx('authInner')}>
          <input
            {...tokenField}
            type="text"
            className={cx('input', { error: tokenMeta.invalid })}
            placeholder={'인증코드를 입력해 주세요.'}
            disabled={certState === 'SUCCEESS' || emailMeta.invalid}
          />
          <div className={cx('btnInfoArea')}>
            {certState === 'ING' && <span className={cx('timer')}>{getRemainingTimeFormat('mm:ss')}</span>}
            <button
              type="button"
              className={cx('btnInfo')}
              onClick={onClickCheckCert}
              disabled={!tokenField.value || !(certState === 'ING')}
            >
              확인
            </button>
          </div>
          <div className={cx('noticeTextArea')}>
            <span className={cx('noticeText', 'error')}>
              <ErrorMessage name={tokenFieldName} />
            </span>
          </div>
        </div>
      </div>
    </FieldContainer>
  );
};

export default EmailAuth;
